import { AuthProvider, } from "react-admin";
import { apiUrl as serverUrl } from "./config";
import { dataProvider } from "./dataProvider";

let apiUrl = serverUrl

// Token 管理工具类
class TokenManager {
  private static instance: TokenManager;
  private renewing: boolean = false;
  private renewPromise: Promise<string | null> | null = null;

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  // 检查 token 是否需要更新
  shouldRenewToken(): boolean {
    const expiration = localStorage.getItem("expiration");
    if (!expiration) return false;

    const expireTime = parseInt(expiration, 10);
    const currentTime = Date.now();
    const thirtyMinutes = 30 * 60 * 1000; // 30分钟

    return (expireTime - currentTime) <= thirtyMinutes;
  }

  // 检查 token 是否已过期
  isTokenExpired(): boolean {
    const expiration = localStorage.getItem("expiration");
    if (!expiration) return true;

    const expireTime = parseInt(expiration, 10);
    return Date.now() >= expireTime;
  }

  // 更新 token
  async renewToken(): Promise<string | null> {
    // 如果正在更新，返回同一个 Promise
    if (this.renewing && this.renewPromise) {
      return this.renewPromise;
    }

    this.renewing = true;
    this.renewPromise = this._doRenewToken();

    try {
      const newToken = await this.renewPromise;
      return newToken;
    } finally {
      this.renewing = false;
      this.renewPromise = null;
    }
  }

  private async _doRenewToken(): Promise<string | null> {
    try {
      console.log('正在自动更新 token...');
      const token = localStorage.getItem("refreshToken");
      const response = await fetch(apiUrl + '/account/pc/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("authToken")}`
        },
        body: JSON.stringify({ refreshToken: token }),
      });

      const res = await response.json();

      if (!response.ok || res.code !== 0) {
        console.error('Token 更新失败:', res);
        // 更新失败，清除本地存储，要求重新登录
        this.clearAuthData();
        return null;
      }

      // 更新本地存储的 token 和过期时间
      localStorage.setItem("authToken", res.data.accessToken);
      localStorage.setItem("expiration", res.data.expiration);
      localStorage.setItem("refreshToken", res.data.refreshToken)

      console.log('Token 更新成功');
      return res.data.accessToken;
    } catch (error) {
      console.error('Token 更新异常:', error);
      this.clearAuthData();
      return null;
    }
  }

  // 清除认证数据
  clearAuthData(): void {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("username");
    localStorage.removeItem("permissions");
    localStorage.removeItem("expiration");
  }

  // 设置 token 数据
  setTokenData(token: string, refreshToken: string, expiration: string, username: string, permissions: string): void {
    localStorage.setItem("authToken", token);
    localStorage.setItem("expiration", expiration);
    localStorage.setItem("refreshToken", refreshToken)
    localStorage.setItem("username", username);
    localStorage.setItem("permissions", permissions);
  }
}

const tokenManager = TokenManager.getInstance();

export const authProvider: AuthProvider = {
  async login({ username, password, captcha, tokenId }) {
    const response = await fetch(apiUrl + '/account/pc/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, tokenId, captcha }),
    });

    const res = await response.json();
    if (!response.ok || res.code !== 0) {
      console.log(res)
      if (res.code === 2012) {
        throw new Error(`用户不存在或密码错误`);
      } else if (res.code === 1011) {
        throw new Error(`验证码已过期，请重新获取验证码`);
      }
      //throw new Error(`获取验证码操作频繁，请1分钟后再试，谢谢！`);
      throw new Error(`[${res.code}]${res.msg}`);
    }

    // 使用 TokenManager 设置 token 数据
    tokenManager.setTokenData(
      res.data.accessToken,
      res.data.refreshToken,
      res.data.expiration,
      username,
      '' // 权限信息稍后设置
    );

    await dataProvider.getUserInfo().then((res) => {
      localStorage.setItem("permissions", res.roleCode);
      return Promise.resolve();
    })
  },

  async logout() {
    console.log('auth.logout')
    tokenManager.clearAuthData();
    return Promise.resolve();
  },

  async checkError(error) {
    console.log('checkError', error)
    if (error.status === 401 || error.status === 403) {
      tokenManager.clearAuthData();
      throw new Error(error.message || "登录已过期，请重新登录");
    }
    return Promise.resolve();
  },

  async checkAuth() {
    console.log('checkAuth')

    // 检查 token 是否过期
    if (tokenManager.isTokenExpired()) {
      tokenManager.clearAuthData();
      throw new Error("登录已过期，请重新登录");
    }

    // 检查是否需要更新 token
    if (tokenManager.shouldRenewToken()) {
      try {
        const newToken = await tokenManager.renewToken();
        if (!newToken) {
          throw new Error("Token 更新失败，请重新登录");
        }
      } catch (error) {
        throw new Error("自动登录失败，请重新登录");
      }
    }
  },

  async getIdentity() {
    try {
      const username = localStorage.getItem('username');
      return Promise.resolve({
        id: username || 'unknown',
        fullName: username || '未知用户',
      });
    } catch (error) {
      return Promise.reject(error);
    }
  },

  async getPermissions() {
    return Promise.resolve();
  }
};

// 导出 tokenManager 供其他地方使用
export { tokenManager };